/**
 * What: Draws a Worker-sent `checkbox` block — a tick box with its optional category badge above it — and reports the box as
 *   `'on'` (ticked) or `'off'` (unticked) through the ordinary edit path.
 * Transport / data-flow: Pure presentation over the payload. `checked` is already the pending-overlaid state, so this block
 *   holds no tick state and no rule about when to trust the Worker.
 */
import type { BuilderBlock } from '@testfeed/shopify-contracts'
import type { OnEdit } from './SectionBlocks'

type Block = Extract<BuilderBlock, { kind: 'checkbox' }>

export function CheckboxBlock({
  block,
  onEdit,
  checked,
  disabled,
  error,
}: {
  block: Block
  onEdit: OnEdit
  checked: boolean
  disabled: boolean
  /** The Worker's error for this box, already withheld while the box has an unanswered edit (`visibleMessage`). */
  error: string | undefined
}) {
  return (
    <s-stack direction="block" gap="small-100">
      {block.badge ? (
        <s-box>
          <s-badge tone={block.badge.tone ?? undefined}>{block.badge.text}</s-badge>
        </s-box>
      ) : null}
      <s-checkbox
        label={block.label}
        details={block.details ?? undefined}
        checked={checked}
        disabled={disabled}
        error={error}
        onInput={(event) => onEdit(block.key, event.currentTarget.checked ? 'on' : 'off')}
      />
    </s-stack>
  )
}
